import type { Subtask } from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api';
import type { CaretCsvDefinition } from '@shared/csv/use-caret-utils';
import { createCaretCsvHandler, createCsvFieldHelpers } from '@shared/csv/use-caret-utils';

import {
    TASK_CSV_EXPECTED_COLUMN_COUNT,
    TASK_CSV_FIELD,
    TASK_CSV_HEADER,
    TASK_CSV_ISSUES_FILE_NAME,
    TASK_CSV_TEMPLATE_FILE_NAME,
} from './constants';

const { formatField } = createCsvFieldHelpers();

interface TaskCsvMaxLengths {
    descriptionMaxLength?: number;
    guidelineMaxLength?: number;
}

const validateTaskCsvRow = (
    fields: string[],
    { descriptionMaxLength, guidelineMaxLength }: TaskCsvMaxLengths,
): string | null => {
    if (fields.length !== TASK_CSV_EXPECTED_COLUMN_COUNT) {
        return `Expected ${TASK_CSV_EXPECTED_COLUMN_COUNT} columns, received ${fields.length}`;
    }

    const description = fields[TASK_CSV_FIELD.description]?.trim();
    const guideline = fields[TASK_CSV_FIELD.guideline]?.trim();

    if (!description) {
        return 'Description is required';
    }

    if (!guideline) {
        return 'Assessor guideline is required';
    }

    if (descriptionMaxLength !== undefined && description.length > descriptionMaxLength) {
        return `Description cannot exceed ${descriptionMaxLength} characters`;
    }

    if (guidelineMaxLength !== undefined && guideline.length > guidelineMaxLength) {
        return `Assessor guideline cannot exceed ${guidelineMaxLength} characters`;
    }

    return null;
};

const parseTaskRow = (fields: string[]): Subtask => ({
    description: fields[TASK_CSV_FIELD.description].trim(),
    guideline: fields[TASK_CSV_FIELD.guideline].trim(),
});

const formatTaskRow = (subtask: Subtask): string[] => [
    formatField(subtask.description),
    formatField(subtask.guideline),
];

export const taskCsvDefinition: CaretCsvDefinition<Subtask> = {
    header: TASK_CSV_HEADER,
    parseRow: parseTaskRow,
    formatRow: formatTaskRow,
    isDuplicate: () => false,
    templateFileName: TASK_CSV_TEMPLATE_FILE_NAME,
    exportFileName: 'task.csv',
    issuesFileName: TASK_CSV_ISSUES_FILE_NAME,
};

export const createTaskCsvHandler = (maxLengths: TaskCsvMaxLengths) =>
    createCaretCsvHandler({
        ...taskCsvDefinition,
        validateRow: (fields) => validateTaskCsvRow(fields, maxLengths),
    });
